import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';

import { SurfaceError } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import { holdsRole } from '../_shared/roles';
import { copy } from './copy';
import { errors } from './errors';
import { adminUserKey, useAdminUser } from './queries';
import type { AskConfirm } from './types';
import { BiographySection } from './UserDetail.Biography';
import { CaseHistorySection } from './UserDetail.CaseHistory';
import { EntitlementsSection } from './UserDetail.Entitlements';
import { ProfileSection } from './UserDetail.Profile';
import { UserDetailSkeleton } from './UserDetail.Skeleton';
import { RolesSection } from './UserDetail.Roles';

/**
 * `/users/:userId` — one account seen from the operator's side.
 *
 * **Three independent reads across three services**, composed here and never
 * merged into one: the profile is `backend-auth`'s, the entitlements are
 * `backend-finance`'s, and the case history is `backend-support`'s. Each panel
 * carries its own skeleton, its own empty state, and its own banner, so one
 * peer's outage takes one band down and leaves the rest of the page usable.
 *
 * **Four verbs, and the catalog grants exactly those four**: change the roles,
 * edit the biography, grant an entitlement, block the account. Everything else
 * on the page is read — the name, the handle, the avatar, and the mobile number
 * are all edited by their owner, and the number's edit is a change of credential
 * that completes against a code sent to the new number.
 *
 * **The biography section is absent, not empty, for a role-less account.** The
 * catalog gives a subscriber no biography, `backend-auth` omits the key rather
 * than serving `null`, and the service refuses the write with a `409`. Rendering
 * an empty section would invite an operator to write a paragraph nothing
 * displays.
 *
 * **One `<ConfirmDialog>` serves the whole page.** Each section writes its own
 * heading, body, and commit label and hands them here with the write to run;
 * four separate dialogs would be four focus traps whose copy drifts apart.
 */
export function UserDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const query = useAdminUser(userId);
  const queryClient = useQueryClient();

  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [committing, setCommitting] = useState(false);

  // The pending decision is held in a ref rather than in state: it is read once,
  // on accept, and re-rendering the page because a callback changed identity
  // would remount the dialog that is currently open.
  const pending = useRef<{
    commit: () => Promise<void> | void;
    onError: (error: unknown) => void;
  } | null>(null);

  const askConfirm = useCallback<AskConfirm>((subject, commit, onError) => {
    pending.current = { commit, onError };
    setConfirmSubject(subject);
  }, []);

  function dismiss() {
    pending.current = null;
    setConfirmSubject(null);
  }

  async function accept() {
    const decision = pending.current;
    if (!decision) return;

    setCommitting(true);
    try {
      await decision.commit();
      dismiss();
    } catch (error) {
      dismiss();
      decision.onError(error);
    } finally {
      setCommitting(false);
    }
  }

  const user = query.data;

  return (
    <>
      {/*
        Three levels once the account is known, two until then. The trail names
        the account rather than a placeholder: a third crumb repeating the
        register's own label would announce a location nobody is at, and it
        would collide with the crumb above it.
      */}
      <Breadcrumb
        items={[
          { label: sharedCopy.crumbs.console, to: '/' },
          { label: sharedCopy.crumbs.users, ...(user ? { to: '/users' } : {}) },
          ...(user ? [{ label: user.display_name }] : []),
        ]}
      />

      {query.isError ? (
        <div className="mt-6">
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: adminUserKey(userId) })}
          />
        </div>
      ) : null}

      {query.isPending ? (
        <div className="mt-6">
          <UserDetailSkeleton />
        </div>
      ) : null}

      {/*
        There is no separate not-found branch: an unknown identifier is a `404
        AUTH_NOT_FOUND` on the read, and the banner above already carries the
        sentence for it. A second empty state here would render for a case the
        query never produces.
      */}
      {user ? (
        <>
          <div className="mb-6 mt-3 grid gap-stack-tight">
            {/*
              The `<h1>` **is** the subject, so there is no page head above it:
              two first-level headings for one document is the defect that rule
              exists to prevent.
            */}
            <h1 className="font-display text-h1 font-bold">{user.display_name}</h1>
            <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
          </div>

          <ProfileSection user={user} askConfirm={askConfirm} />

          <RolesSection user={user} askConfirm={askConfirm} />

          {holdsRole(user.role) ? <BiographySection user={user} /> : null}

          <EntitlementsSection userId={user.id} userName={user.display_name} />

          <CaseHistorySection userId={user.id} />
        </>
      ) : null}

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={committing}
        onConfirm={() => void accept()}
        onCancel={dismiss}
      />
    </>
  );
}
